import { lazy, Suspense, useEffect, useState } from 'react'
import { useCurrentHostname, useCurrentPathname } from '../marketingPrerender'
import { BUSINESS_PROFESSION_ROUTE_META } from './marketingSeo.js'
import { establishedProfessionIds, getProfessionBySlug } from './professions/catalogue.js'
import { PROFESSION_CHILD_SLUGS } from './professions/professionSeo.js'
import { legacyMarketingPathForHost } from '../../../utils/siteDomains'
import './index.css'

const PageApp = lazy(() => import('./PageApp'))
const PageAllFeatures = lazy(() => import('./PageAllFeatures'))
const PageFeatureDetail = lazy(() => import('./PageFeatureDetail'))
const PagePricing = lazy(() => import('./PagePricing'))
const PageAppContact = lazy(() => import('./PageAppContact'))
const PageSwitch = lazy(() => import('./PageSwitch'))
const PageMobileBusinessTypes = lazy(() => import('./PageMobileBusinessTypes'))
const PageProfessionIndex = lazy(() => import('./professions/PageProfessionIndex'))
const PageProfessionCluster = lazy(() => import('./professions/PageProfessionCluster'))
const PageChiropodists = lazy(() => import('./PageChiropodists'))
const PageAllMobileBusinessesOnlineBooking = lazy(() =>
  import('./PageChiropodists').then(module => ({
    default: module.PageAllMobileBusinessesOnlineBooking,
  })),
)
const PageMobileHairdressers = lazy(() => import('./PageMobileHairdressers'))
const PageCleaners = lazy(() => import('./PageCleaners'))
const PageEarWaxRemovalSpecialists = lazy(() => import('./PageEarWaxRemovalSpecialists'))
const PageGardeners = lazy(() => import('./PageGardeners'))
const LegacyNewHubApp = lazy(() => import('./App'))

const knownProfessionPaths = new Set(
  BUSINESS_PROFESSION_ROUTE_META.map(route => route.path.replace(/\/+$/, '')),
)

const featureAliases: Record<string, string> = {
  invoices: 'invoices-and-receipts',
  mileage: 'mileage-tracking',
  booking: 'online-booking-page',
  services: 'services-and-prices',
  clients: 'client-records',
  documents: 'documents-and-notes',
  profile: 'public-profile',
  routing: 'smart-routing',
  iphone: 'tap-to-pay',
  android: 'tap-to-pay',
  'tap-to-pay-on-iphone': 'tap-to-pay',
  'tap-to-pay-on-android': 'tap-to-pay',
  'auto-reminders': 'reminders',
  'automatic-reminders': 'reminders',
  'automated-reminders': 'reminders',
}

function normalizeRoutePath(pathname: string) {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.replace(/\/+$/, '')
  }
  return pathname
}

function useRoute() {
  const routePath = () => normalizeRoutePath(legacyMarketingPathForHost(window.location.pathname, window.location.hostname))
  const initialPathname = useCurrentPathname()
  const initialHostname = useCurrentHostname()
  const [path, setPath] = useState(() => normalizeRoutePath(legacyMarketingPathForHost(initialPathname, initialHostname)))

  useEffect(() => {
    const handler = () => setPath(routePath())
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [])

  return path
}

function matchBusinessProfessionPath(path: string, slugs: string[]) {
  for (const slug of slugs) {
    const base = `/for-businesses/${slug}`
    if (path === base) return ''
    if (path.startsWith(`${base}/`)) return path.slice(base.length + 1)
  }
  return null
}

function isKnownProfessionPath(pluralSlug: string, suffix: string) {
  return knownProfessionPaths.has(
    `/for-businesses/${pluralSlug}${suffix ? `/${suffix}` : ''}`,
  )
}

function resolveKnownRoute(path: string) {
  // internalMarketingPath maps every host's "/" to its site's home; "/" itself
  // only reaches here from a direct render of the route tree.
  if (path === '/' || path === '/for-businesses') return <PageApp />
  if (path === '/for-businesses/features') return <PageAllFeatures />
  if (path === '/for-businesses/pricing') return <PagePricing />
  if (path === '/for-businesses/contact') return <PageAppContact />
  if (path === '/for-businesses/switch') return <PageSwitch />
  if (path === '/for-businesses/all-mobile-businesses') return <PageMobileBusinessTypes />
  if (path === '/for-businesses/professions') return <PageProfessionIndex />
  if (path.startsWith('/for-businesses/professions/')) {
    const slug = path.slice('/for-businesses/professions/'.length)
    const profession = slug && !slug.includes('/') ? getProfessionBySlug(slug) : undefined
    return profession && !establishedProfessionIds.has(profession.id)
      ? <PageProfessionCluster profession={profession} slug="" />
      : <LegacyNewHubApp />
  }
  if (path === '/for-businesses/all-mobile-businesses/online-booking') {
    return <PageAllMobileBusinessesOnlineBooking />
  }

  const chiropodistSlug = matchBusinessProfessionPath(path, ['chiropodists', 'chiropodist'])
  if (chiropodistSlug !== null) {
    return isKnownProfessionPath('chiropodists', chiropodistSlug)
      ? <PageChiropodists slug={chiropodistSlug} />
      : <LegacyNewHubApp />
  }

  const mobileHairdresserSlug = matchBusinessProfessionPath(path, ['mobile-hairdressers', 'mobile-hairdresser'])
  if (mobileHairdresserSlug !== null) {
    return isKnownProfessionPath('mobile-hairdressers', mobileHairdresserSlug)
      ? <PageMobileHairdressers slug={mobileHairdresserSlug} />
      : <LegacyNewHubApp />
  }

  const cleanerSlug = matchBusinessProfessionPath(path, ['cleaners', 'cleaner'])
  if (cleanerSlug !== null) {
    return isKnownProfessionPath('cleaners', cleanerSlug)
      ? <PageCleaners slug={cleanerSlug} />
      : <LegacyNewHubApp />
  }

  const gardenerSlug = matchBusinessProfessionPath(path, ['gardeners', 'gardener'])
  if (gardenerSlug !== null) {
    return isKnownProfessionPath('gardeners', gardenerSlug)
      ? <PageGardeners slug={gardenerSlug} />
      : <LegacyNewHubApp />
  }

  const earWaxRemovalSpecialistSlug = matchBusinessProfessionPath(path, [
    'ear-wax-removal-specialists',
    'ear-wax-removal-specialist',
  ])
  if (earWaxRemovalSpecialistSlug !== null) {
    return isKnownProfessionPath('ear-wax-removal-specialists', earWaxRemovalSpecialistSlug)
      ? <PageEarWaxRemovalSpecialists slug={earWaxRemovalSpecialistSlug} />
      : <LegacyNewHubApp />
  }

  const professionParts = path.replace(/^\/for-businesses\//, '').split('/')
  if (professionParts.length <= 2) {
    const profession = getProfessionBySlug(professionParts[0])
    const childSlug = professionParts[1] || ''
    if (profession && !establishedProfessionIds.has(profession.id) &&
      (!childSlug || PROFESSION_CHILD_SLUGS.includes(childSlug))) {
      return <PageProfessionCluster profession={profession} slug={childSlug} />
    }
  }

  if (path.startsWith('/for-businesses/features/')) {
    const rawSlug = path.replace('/for-businesses/features/', '')
    const slug = featureAliases[rawSlug] || rawSlug
    return <PageFeatureDetail key={slug} slug={slug} />
  }

  return <LegacyNewHubApp />
}

export default function OptimizedApp() {
  const path = useRoute()
  // Lets the app-wide click tracker send cta_click for marketing pages only
  // (utils/ctaTracking.js).
  useEffect(() => {
    document.body.dataset.offlicoMarketing = '1'
    return () => {
      delete document.body.dataset.offlicoMarketing
    }
  }, [])
  return <Suspense fallback={null}>{resolveKnownRoute(path)}</Suspense>
}
